import { OrdenCompraDetalle } from '@/components/solicitudes-contado/OrdenCompraDetalle';
import { useOrdenesCompraBC } from '@/hooks/useSolicitudesContado';
import { cn } from '@/lib/utils';
import { type SolicitudContado, formatearCRC, formatearUSD } from '@adelante/shared';
import { CaretDown, CaretUp, Check } from '@phosphor-icons/react';
import { useState } from 'react';

interface Props {
  solicitud: SolicitudContado;
  /** El administrador puede marcar cuáles OC se pagan (solicitud pendiente). */
  editable: boolean;
  /** Números de OC marcadas para pagar. */
  seleccion: string[];
  /** Nueva selección y la suma de sus montos (null si alguna OC no tiene monto conocido). */
  onCambio: (seleccion: string[], suma: number | null) => void;
}

/**
 * Las OC de una solicitud, para desplegar bajo su fila: cada una con su obra, su monto y el detalle de
 * sus líneas. Si se puede editar, marcar u desmarcar una OC recalcula el monto aprobado de la solicitud.
 */
export function OrdenesDeSolicitud({ solicitud, editable, seleccion, onCambio }: Props) {
  const soloFacturas = solicitud.tipoDocumento === 'FACTURA';
  const unidad =
    solicitud.tipoDocumento === 'MIXTO' ? 'documentos' : soloFacturas ? 'facturas' : 'OC';
  const dolares = !!solicitud.currencyCode && solicitud.currencyCode !== 'CRC';
  const formato = (m: number) => (dolares ? formatearUSD(m) : formatearCRC(m));
  const { data } = useOrdenesCompraBC(soloFacturas ? null : solicitud.vendorCode);
  const vivas = new Map((data?.data ?? []).map((o) => [o.numero, o]));
  const [detalle, setDetalle] = useState<string | null>(null);

  const filas = solicitud.documentos.map((d) => {
    const viva = vivas.get(d.numero);
    return { ...d, monto: d.monto ?? viva?.monto ?? null, obra: viva?.obraCodigo ?? null };
  });
  const marcadas = filas.filter((f) => seleccion.includes(f.numero));
  const sinMarcar = filas.filter((f) => !seleccion.includes(f.numero));
  const suma = (lista: typeof filas) =>
    lista.every((f) => f.monto !== null) ? lista.reduce((a, f) => a + (f.monto ?? 0), 0) : null;
  const sumaMarcadas = suma(marcadas);
  const sumaSinMarcar = suma(sinMarcar);

  function alternar(numero: string) {
    const nueva = seleccion.includes(numero)
      ? seleccion.filter((n) => n !== numero)
      : [...seleccion, numero];
    onCambio(nueva, suma(filas.filter((f) => nueva.includes(f.numero))));
  }

  return (
    <div className="space-y-2 text-sm">
      <div className="overflow-hidden rounded-md border bg-background">
        {filas.map((f) => (
          <div key={f.numero} className="border-b last:border-b-0">
            <div className="flex items-center gap-3 px-3 py-2">
              {editable ? (
                <input
                  type="checkbox"
                  aria-label={`Pagar ${f.numero}`}
                  checked={seleccion.includes(f.numero)}
                  onChange={() => alternar(f.numero)}
                />
              ) : (
                <span className="flex h-4 w-4 items-center justify-center">
                  {f.aprobada && (
                    <Check
                      className="h-3.5 w-3.5 text-green-700"
                      weight="bold"
                      aria-label="Aprobada"
                    />
                  )}
                </span>
              )}
              <span className="w-24 font-medium">{f.numero}</span>
              {solicitud.tipoDocumento === 'MIXTO' && (
                <span className="w-12 text-xs text-muted-foreground">
                  {f.entryNo ? 'Fact.' : 'OC'}
                </span>
              )}
              <span className="w-24 text-xs text-muted-foreground">{f.obra ?? '—'}</span>
              <span className="ml-auto tabular-nums">
                {f.monto !== null ? formato(f.monto) : 'Monto desconocido'}
              </span>
              {!f.entryNo && (
                <button
                  type="button"
                  aria-expanded={detalle === f.numero}
                  onClick={() => setDetalle(detalle === f.numero ? null : f.numero)}
                  className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Detalle
                  {detalle === f.numero ? (
                    <CaretUp className="h-3 w-3" weight="bold" aria-hidden="true" />
                  ) : (
                    <CaretDown className="h-3 w-3" weight="bold" aria-hidden="true" />
                  )}
                </button>
              )}
            </div>
            {!f.entryNo && detalle === f.numero && (
              <div className="border-t bg-muted/30 px-4 py-3">
                <OrdenCompraDetalle numero={f.numero} />
              </div>
            )}
          </div>
        ))}
      </div>

      {editable && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="text-muted-foreground">
            {marcadas.length} de {filas.length} marcadas
          </span>
          <span className={cn('font-medium', sumaMarcadas === null && 'text-amber-700')}>
            {sumaMarcadas !== null
              ? `Suma de las marcadas: ${formato(sumaMarcadas)}`
              : `Alguna de las ${unidad} no tiene monto: escribí el monto a aprobar`}
          </span>
          {sinMarcar.length > 0 && (
            <span className="text-amber-700">
              Las {sinMarcar.length} {unidad} sin marcar quedarán como una solicitud nueva pendiente
              {sumaSinMarcar !== null ? ` por ${formato(sumaSinMarcar)}` : ''}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
